'use client'

import { AnimatePresence, MotionProps, motion, Target, TargetAndTransition } from 'motion/react'
import Link from 'next/link'
import { useLocale } from 'next-intl'
import { useId, useMemo } from 'react'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Locale } from '@/setup/locales'
import { getLinkHref, ILinkParams, IUrlPattern } from '@/setup/routing'
import { IChildrenProps } from '@/typings/children-props'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'
import { rgba } from '@/utils/styles/rgba'
import { useMobile } from '@/utils/window/use-mobile'

export type IButtonStyleType =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'ghost'
    | 'promo'
    | 'invisible'
    | 'unstyled'
    | 'shaded'

export type IButtonShape = 'regular' | 'pill' | 'circle'

/** Sizes from the Umami Figma Button spec: regular 52px, small 42px */
export type IButtonSize = 'regular' | 'small'

type IButtonProps = IChildrenProps & {
    id?: string
    type?: 'button' | 'submit'
    styleType: IButtonStyleType
    className?: string
    shape?: IButtonShape
    size?: IButtonSize
    disabled?: boolean
    isLoading?: boolean
    setIsLoading?: (isLoading: boolean) => void
    disableAnimations?: boolean

    /**
     * Possibilty to override used colors
     */
    backgroundColor?: string
    contentColor?: string
    borderColor?: string
    boxShadow?: string
    height?: string
    width?: string
    style?: React.CSSProperties
} & (
        | {
              onClick: () => void | Promise<unknown>
          }
        | {
              href: ILinkParams<IUrlPattern> | { externalUrl: string }
              target?: '_blank' | '_self'
          }
    )

export const Button = (props: IButtonProps) => {
    const componentId = useId()
    const locale = useLocale()

    const onClick = useMemo(() => {
        if (!('onClick' in props)) return

        return () => {
            if (props.isLoading) return

            const res = props.onClick()

            if (res instanceof Promise) {
                props.setIsLoading?.(true)

                res.finally(() => {
                    props.setIsLoading?.(false)
                })
            }
        }
    }, [props])

    // Handle use cases where the button triggers an action or navigates to a different page
    const actionProps = useMemo(() => {
        if (props.disabled) return {}

        if ('onClick' in props) {
            return {
                onClick,
            }
        }

        return {
            href: props.href,
            target: props.target ?? '_self',
        }
    }, [props, onClick])

    const isMobile = useMobile()

    const { backgroundColor, contentColor, borderColor, borderWidth, boxShadow, animations } =
        useMemo((): Record<
            'backgroundColor' | 'contentColor' | 'borderColor' | 'borderWidth' | 'boxShadow',
            string | undefined
        > & {
            animations?: {
                initial?: Target
                animate?: Target | TargetAndTransition
                whileHover?: Target | TargetAndTransition
                whileTap?: Target | TargetAndTransition
                transition?: MotionProps['transition']
            }
        } => {
            // On desktop this is the only feedback for styleTypes without their own
            // hover colors (tertiary, promo, invisible and color-overridden buttons)
            const defaultScaleAnimation = isMobile
                ? {
                      initial: { scale: 1 },
                      animate: { scale: 1 },
                      whileTap: { scale: 0.96 },
                      transition: { scale: { type: 'spring' as const, duration: 0.35 } },
                  }
                : {
                      initial: { scale: 1 },
                      animate: { scale: 1 },
                      whileHover: { scale: 1.02 },
                      whileTap: { scale: 0.97 },
                      transition: { scale: { type: 'spring' as const, duration: 0.3 } },
                  }

            const computedStyles = run(() => {
                switch (props.styleType) {
                    case 'primary':
                        return {
                            backgroundColor: COLORS.bg.brand,
                            contentColor: COLORS.fg.primary,
                            borderColor: COLORS.bg.brand,
                            animations: {
                                ...defaultScaleAnimation,
                                whileHover: {
                                    backgroundColor: COLORS.bg.brandStrong,
                                    borderColor: COLORS.bg.brandStrong,
                                },
                                whileTap: {
                                    ...defaultScaleAnimation.whileTap,
                                    backgroundColor: COLORS.bg.brandStrong,
                                    borderColor: COLORS.bg.brandStrong,
                                },
                                transition: {
                                    ...defaultScaleAnimation.transition,
                                    backgroundColor: { duration: 0.1, ease: 'easeOut' as const },
                                    borderColor: { duration: 0.1, ease: 'easeOut' as const },
                                },
                            },
                        }

                    case 'secondary':
                        return {
                            backgroundColor: COLORS.bg.neutralSurface,
                            contentColor: COLORS.fg.primary,
                            borderColor: COLORS.bg.neutralSurface,
                            animations: {
                                ...defaultScaleAnimation,
                                whileHover: {
                                    backgroundColor: COLORS.bg.neutralSurfaceMedium,
                                    borderColor: COLORS.bg.neutralSurfaceMedium,
                                },
                                whileTap: {
                                    ...defaultScaleAnimation.whileTap,
                                    backgroundColor: COLORS.bg.neutralSurfaceMedium,
                                    borderColor: COLORS.bg.neutralSurfaceMedium,
                                },
                                transition: {
                                    ...defaultScaleAnimation.transition,
                                    backgroundColor: { duration: 0.1, ease: 'easeOut' as const },
                                    borderColor: { duration: 0.1, ease: 'easeOut' as const },
                                },
                            },
                        }

                    case 'tertiary':
                        return {
                            backgroundColor: COLORS.bg.overlayWhiteSubtle,
                            contentColor: COLORS.fg.primary,
                            borderColor: COLORS.border.secondary,
                            borderWidth: '2px',
                        }

                    case 'promo':
                        return {
                            backgroundColor: COLORS.bg.overlayBrand,
                            contentColor: COLORS.fg.brand,
                            borderColor: 'transparent',
                        }

                    case 'ghost':
                        return {
                            backgroundColor: COLORS.bg.overlayWhiteSubtle,
                            contentColor: COLORS.fg.secondary,
                            borderColor: 'transparent',
                            animations: {
                                ...defaultScaleAnimation,
                                whileHover: {
                                    color: COLORS.fg.primary,
                                },
                                whileTap: {
                                    ...defaultScaleAnimation.whileTap,
                                    color: COLORS.fg.primary,
                                },
                                transition: {
                                    ...defaultScaleAnimation.transition,
                                    color: { duration: 0.15, ease: 'easeOut' as const },
                                },
                            },
                        }

                    case 'invisible':
                        return {
                            backgroundColor: rgba(0, 0, 0, 0),
                            contentColor: COLORS.fg.primary,
                            borderColor: 'transparent',
                        }
                    case 'shaded':
                        return {
                            backgroundColor: 'rgba(0, 0, 0, 0)',
                            contentColor: COLORS.fg.primary,
                            borderColor: 'transparent',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                            animations: {
                                initial: {
                                    ...defaultScaleAnimation.initial,
                                    backgroundColor: 'rgba(0, 0, 0, 0)',
                                    boxShadow: '0px 8px 12px rgba(0, 0, 0, 0.12)',
                                },
                                animate: {
                                    ...defaultScaleAnimation.animate,
                                    backgroundColor: 'rgba(0, 0, 0, 0)',
                                    boxShadow: '0px 6px 8px 2px rgba(0, 0, 0, 0.1)',
                                },
                                whileHover: {
                                    backgroundColor: 'rgba(0, 0, 0, 0)',
                                    boxShadow: '0px 10px 28px rgba(0, 0, 0, 0.12)',
                                },
                                whileTap: {
                                    ...defaultScaleAnimation.whileTap,
                                    backgroundColor: 'rgba(0, 0, 0, 0)',
                                    boxShadow: '0px 5px 14px rgba(0, 0, 0, 0.08)',
                                },
                                transition: {
                                    ...defaultScaleAnimation.transition,
                                    backgroundColor: { duration: 0.15, ease: 'easeOut' as const },
                                    boxShadow: { duration: 0.2, ease: 'easeOut' as const },
                                },
                            },
                        }
                }
            })

            // Disabled primary has its own colors per the Figma spec instead of
            // the generic opacity treatment used by the other variants
            const disabledStyles =
                props.disabled && props.styleType === 'primary'
                    ? {
                          backgroundColor: COLORS.bg.brandSubtle,
                          contentColor: COLORS.fg.disabled,
                          borderColor: COLORS.bg.brandSubtle,
                      }
                    : undefined

            // Color prop overrides must also win while hovering and tapping: the
            // styleType animations carry their own hardcoded colors, so every
            // overridden property is stripped from the animation states (the
            // scale and shadow parts stay)
            const rawAnimations: {
                initial?: Target
                animate?: Target | TargetAndTransition
                whileHover?: Target | TargetAndTransition
                whileTap?: Target | TargetAndTransition
                transition?: MotionProps['transition']
            } = computedStyles?.animations ?? defaultScaleAnimation

            const overriddenAnimationKeys: Array<'backgroundColor' | 'borderColor' | 'color'> = []
            if (props.backgroundColor) overriddenAnimationKeys.push('backgroundColor')
            if (props.borderColor) overriddenAnimationKeys.push('borderColor')
            if (props.contentColor) overriddenAnimationKeys.push('color')

            const stripOverridden = <T extends Target | TargetAndTransition | undefined>(
                target: T
            ): T => {
                if (!target) return target

                const clone = { ...target }

                for (const key of overriddenAnimationKeys) {
                    delete clone[key]
                }

                return clone
            }

            const animations = overriddenAnimationKeys.length
                ? {
                      ...rawAnimations,
                      initial: stripOverridden(rawAnimations.initial),
                      animate: stripOverridden(rawAnimations.animate),
                      whileHover: stripOverridden(rawAnimations.whileHover),
                      whileTap: stripOverridden(rawAnimations.whileTap),
                  }
                : rawAnimations

            // If there are props provided to alter the colors, use them instead of the computed ones
            return {
                backgroundColor:
                    props.backgroundColor ??
                    disabledStyles?.backgroundColor ??
                    computedStyles?.backgroundColor,
                contentColor:
                    props.contentColor ??
                    disabledStyles?.contentColor ??
                    computedStyles?.contentColor,
                borderColor:
                    props.borderColor ?? disabledStyles?.borderColor ?? computedStyles?.borderColor,
                borderWidth: computedStyles?.borderWidth,
                animations,
                boxShadow: props.boxShadow ?? computedStyles?.boxShadow,
            }
        }, [
            props.styleType,
            props.disabled,
            props.backgroundColor,
            props.contentColor,
            props.borderColor,
            props.boxShadow,
            isMobile,
        ])

    const { height, width, borderRadius, paddingInline } = useMemo((): Record<
        'width' | 'height' | 'borderRadius' | 'paddingInline',
        string | undefined
    > => {
        const computedSize = run(() => {
            if (props.styleType === 'unstyled') return {}

            // Umami Figma spec: regular 52px, small 42px, 16px inline padding
            const sizeHeight = props.size === 'small' ? '42px' : '52px'

            switch (props.shape) {
                case 'circle':
                    return {
                        height: sizeHeight,
                        width: sizeHeight,
                        borderRadius: '50%',
                        paddingInline: '0px',
                    }

                case 'pill':
                    return {
                        height: sizeHeight,
                        width: 'fit-content',
                        borderRadius: sizeHeight,
                        paddingInline: '16px',
                    }
                default:
                    return {
                        height: sizeHeight,
                        width: 'fit-content',
                        borderRadius: '16px',
                        paddingInline: '16px',
                    }
            }
        })

        // If there are props provided to alter the size, use them instead of the computed ones
        return {
            height: props.height ?? computedSize.height,
            width: props.width ?? computedSize.width,
            borderRadius: computedSize.borderRadius,
            paddingInline: computedSize.paddingInline,
        }
    }, [props.shape, props.size, props.height, props.width, props.styleType])

    const node = (
        <motion.button
            type={props.type}
            disabled={props.disabled}
            {...(actionProps.onClick ? { onClick: actionProps.onClick } : {})}
            className={cn(
                `border-solid border cursor-pointer flex flex-row items-center justify-center
                focus:outline-hidden relative`,
                props.styleType === 'unstyled'
                    ? 'font-bold'
                    : props.size === 'small'
                      ? 'text-label-md'
                      : 'text-label-lg',
                props.className
            )}
            initial={{
                backgroundColor,
                color: contentColor,
                borderColor,
                boxShadow,
                opacity: props.disabled && props.styleType !== 'primary' ? 0.4 : 1,
                ...animations?.initial,
            }}
            animate={{
                backgroundColor,
                color: contentColor,
                borderColor,
                boxShadow,
                opacity: props.disabled && props.styleType !== 'primary' ? 0.4 : 1,
                ...animations?.animate,
            }}
            whileHover={props.disabled || props.disableAnimations ? {} : animations?.whileHover}
            whileTap={props.disabled || props.disableAnimations ? {} : animations?.whileTap}
            transition={{
                backgroundColor: { duration: 0.1, ease: 'easeOut' },
                color: { duration: 0.1, ease: 'easeOut' },
                borderColor: { duration: 0.1, ease: 'easeOut' },
                opacity: { duration: 0.1, ease: 'easeOut' },
                ...animations?.transition,
            }}
            style={{
                borderRadius,
                borderWidth,
                paddingInline,
                height,
                width,
                minHeight: height,
                minWidth: width,
                cursor: props.disabled ? 'default' : 'pointer',
                ...props.style,
            }}
            id={props.id}
        >
            <motion.span
                initial={{ opacity: props.isLoading ? 0 : 1 }}
                animate={{ opacity: props.isLoading ? 0 : 1 }}
                transition={{ duration: 0.18 }}
                className="flex flex-row items-center justify-center gap-2"
            >
                {props.children}
            </motion.span>

            <AnimatePresence mode="popLayout">
                {props.isLoading && (
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        key={'loading' + componentId}
                        className="absolute transform-center"
                    >
                        <LoadingSpinner size={24} color={contentColor ?? COLORS.fg.primary} />
                    </motion.span>
                )}
            </AnimatePresence>
        </motion.button>
    )

    if ('href' in actionProps && actionProps.href) {
        if ('externalUrl' in actionProps.href) {
            return (
                <a href={actionProps.href.externalUrl} target={actionProps.target}>
                    {node}
                </a>
            )
        }

        return (
            <Link
                href={getLinkHref(actionProps.href, locale as Locale)}
                target={actionProps.target}
            >
                {node}
            </Link>
        )
    }

    return node
}
